import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  ArrowRight,
  Search,
  BookOpen,
  Zap,
  ShieldCheck,
  Sparkles,
  Clock,
} from "lucide-react";
import { chapters, allTutorials, accentClass } from "@/data/tutorials";
import { storage } from "@/lib/storage";

const Stat = ({ k, v }) => (
  <div className="px-4 py-3 brut-border bg-background min-w-[110px]">
    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
      {k}
    </p>
    <p className="font-display font-black text-3xl leading-none mt-1">{v}</p>
  </div>
);

export const HomePage = ({ onOpenSearch }) => {
  const [completedCount, setCompletedCount] = useState(0);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    setCompletedCount(storage.getCompleted().length);
    setRecent(storage.getRecent());
  }, []);

  const recentTutorials = recent
    .map((slug) => allTutorials.find((t) => t.slug === slug))
    .filter(Boolean);

  return (
    <div className="animate-fade-in">
      {/* HERO */}
      <section className="relative px-6 sm:px-10 lg:px-16 pt-12 lg:pt-20 pb-16 border-b-2 border-foreground overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl">
          <div className="inline-flex items-center gap-2 brut-border brut-shadow-sm bg-brut-yellow px-3 py-1 font-mono text-[11px] uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={3} />
            v1.1 · interactive edition
          </div>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mt-6">
            The BRAC Bank{" "}
            <span className="inline-block bg-brut-yellow brut-border px-3 -rotate-1">
              CMS
            </span>
            <br />
            manual that actually{" "}
            <span className="underline decoration-brut-yellow decoration-[6px] underline-offset-4">
              clicks.
            </span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mt-6 max-w-2xl leading-relaxed">
            Every chapter from the official Administration Manual — rebuilt as
            an interactive, searchable, bookmarkable tutorial site. Video
            walkthroughs included. No backend, no nonsense.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={onOpenSearch}
              data-testid="hero-search-button"
              className="group flex-1 sm:flex-none flex items-center justify-between gap-6 brut-border brut-shadow-lg brut-hover bg-background pl-4 pr-3 py-4 text-left min-w-[320px]"
            >
              <span className="flex items-center gap-3">
                <Search className="w-5 h-5" strokeWidth={2.5} />
                <span className="text-muted-foreground">
                  Search 30+ tutorials…
                </span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="kbd">⌘</kbd>
                <kbd className="kbd">K</kbd>
              </span>
            </button>
            <Link
              to={`/tutorial/${chapters[0].sections[0].slug}`}
              data-testid="hero-start-button"
              className="inline-flex items-center justify-center gap-2 brut-border brut-shadow brut-hover bg-foreground text-background px-6 py-4 font-bold uppercase tracking-wider text-sm"
            >
              Start with chapter 1 <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Stat k="Chapters" v={chapters.length} />
            <Stat k="Tutorials" v={allTutorials.length} />
            <Stat k="Videos" v={allTutorials.length} />
            <Stat k="Done" v={completedCount} />
          </div>
        </div>

        <div className="absolute bottom-8 right-8 hidden xl:block animate-pop-in">
          <div className="brut-border brut-shadow-lg bg-brut-rose px-5 py-4 -rotate-3">
            <p className="font-mono text-[10px] uppercase tracking-widest">
              Pro tip
            </p>
            <p className="font-display font-bold text-sm leading-tight mt-1 max-w-[180px]">
              Hit{" "}
              <span className="inline-flex items-center gap-0.5 align-middle">
                <kbd className="kbd">⌘</kbd>
                <kbd className="kbd">K</kbd>
              </span>{" "}
              from anywhere to search.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="px-6 sm:px-10 lg:px-16 py-12 border-b-2 border-foreground diag-stripes">
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            {
              icon: Search,
              title: "Full-text search",
              desc: "Find any step across every chapter in <50ms — no servers required.",
            },
            {
              icon: BookOpen,
              title: "Track your progress",
              desc: "Mark tutorials complete, bookmark favorites, jump back into recent reads.",
            },
            {
              icon: Zap,
              title: "Video + steps, side by side",
              desc: "Each tutorial pairs official Loom walkthroughs with numbered instructions.",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="bg-background brut-border brut-shadow p-6 brut-hover"
            >
              <div className="w-10 h-10 brut-border bg-brut-yellow flex items-center justify-center">
                <f.icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <h3 className="font-display font-extrabold text-xl mt-4">
                {f.title}
              </h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* RECENTLY VIEWED */}
      {recentTutorials.length > 0 && (
        <section className="px-6 sm:px-10 lg:px-16 py-10 border-b-2 border-foreground">
          <div className="flex items-center gap-2 mb-5">
            <Clock className="w-4 h-4" strokeWidth={2.5} />
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Recently viewed
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {recentTutorials.slice(0, 4).map((t) => (
              <Link
                key={t.slug}
                to={`/tutorial/${t.slug}`}
                data-testid={`recent-${t.slug}`}
                className="brut-border bg-background p-4 brut-hover block"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {t.id} · {t.chapterTitle}
                </p>
                <p className="font-display font-bold text-base mt-1.5 leading-tight">
                  {t.title}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CHAPTERS GRID */}
      <section className="px-6 sm:px-10 lg:px-16 py-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Table of Contents
            </p>
            <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-1">
              All chapters.
            </h2>
          </div>
          <ShieldCheck className="w-8 h-8 hidden sm:block" strokeWidth={2.5} />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {chapters.map((ch) => {
            const done = ch.sections.filter((s) =>
              storage.isCompleted(s.slug)
            ).length;
            return (
              <Link
                key={ch.chapter}
                to={`/tutorial/${ch.sections[0].slug}`}
                data-testid={`chapter-card-${ch.chapter}`}
                className="group block bg-background brut-border brut-shadow brut-hover overflow-hidden"
              >
                <div
                  className={`px-5 py-4 border-b-2 border-foreground ${
                    accentClass[ch.chapterAccent]
                  } flex items-center justify-between`}
                >
                  <p className="font-mono text-xs font-bold tracking-widest">
                    CH · {String(ch.chapter).padStart(2, "0")}
                  </p>
                  <p className="font-mono text-xs font-bold">
                    {done}/{ch.sections.length}
                  </p>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-extrabold text-xl leading-tight">
                    {ch.chapterTitle}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">
                    {ch.chapterIntro}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                      {ch.sections.length} tutorial
                      {ch.sections.length === 1 ? "" : "s"}
                    </span>
                    <span className="font-bold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      open <ArrowRight className="w-4 h-4" strokeWidth={3} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <footer className="px-6 sm:px-10 lg:px-16 py-10 border-t-2 border-foreground bg-muted">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-display font-black text-xl">CMS.docs</p>
            <p className="text-xs text-muted-foreground font-mono mt-1">
              Built from BRAC Bank Website CMS Administration Manual v1.1
            </p>
          </div>
          <p className="text-xs text-muted-foreground font-mono">
            Questions? info@singularitybd.com
          </p>
        </div>
      </footer>
    </div>
  );
};
